import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { current } from 'immer'
import { has } from 'lodash'

import { BehaviorSubject } from 'rxjs'

import {
  AiAgent,
  AiPermissions,
  BusinessRuleContext,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { CommandPaletteActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { selectedAiUserTaskTemplateConfig } from '@flowx/designer/shared/store/selectors/template-config.selectors'

import { Command, CommandIcon } from '../command/command.component'
import { AiKeyboardKeyCode, IconType } from '../shortcut/shortcut.component'

const bpmnAnalystSuggestion1Cmd: Command = {
  id: 'bpmn-analyst-suggestion-1',
  title: 'Generate credit card application process',
  icon: CommandIcon.Action,
  action: () => {
    //     TypeAreaEvents.promptSubmit(
    //       `Create credit card approval process with the steps:
    // Application Submission: Customers submit their credit card application
    // Verification and Credit Check: The bank verifies the information provided by the applicant and conducts a credit check to assess their creditworthiness.
    // Approval or Rejection: Based on the information gathered the application is either approved or rejected.
    // Credit Limit Determination: If approved, the bank determines the credit limit for the card
    // Issuance of the Card: Upon approval, the bank issues the credit card to the applicant.`
    //     ),
  },
}

const bpmnAnalystSuggestion2Cmd: Command = {
  id: 'bpmn-analyst-suggestion-2',
  title: 'Generate customer onboarding process',
  icon: CommandIcon.Action,
  action: () => {
    // TypeAreaEvents.promptSubmit(
    //   'Create mortgage process consisting of financial pre-approval, property details, valuation report, final approval, insurance details, signing and disbursement.'
    // ),
  },
}

const bpmnAnalystSuggestion3Cmd: Command = {
  id: 'bpmn-analyst-suggestion-3',
  title: 'Generate KYC process',
  icon: CommandIcon.Action,
  action: () => {
    // TypeAreaEvents.promptSubmit(
    //   'Generate process to collect client information; check if client is new or existing; in case of new, perform kyc check using a send message and receive message; based on kyc result, if marked fraudulent, then stop process; otherwise, start onboarding process (service task)'
    // ),
  },
}

const bpmnAnalystSuggestion4Cmd: Command = {
  id: 'bpmn-analyst-suggestion-4',
  title: 'Collect GDPR agreement and send to customer',
  icon: CommandIcon.Action,
  action: () => {
    // TypeAreaEvents.promptSubmit(
    //   'Generate process to collect customer GDPR agreement, generate signed document and send document to customer email'
    // ),
  },
}

const uiDesignerSuggestion1Cmd: Command = {
  id: 'ui-designer-suggestion-1',
  title: 'Generate applicant’s details form',
  icon: CommandIcon.Action,
  action: () => {
    // TypeAreaEvents.promptSubmit(
    //   'Create form to collect client details, including first name, last name, SSN, date of birth (datepicker), Gender, ID type (dropdown), ID series, ID number, Country of birth (dropdown), City of Birth, Issuing country (dropdown), Issued date (datepicker), expiry date (datepicker), nationality, Issued by; add a Continue button'
    // ),
  },
}

const uiDesignerSuggestion2Cmd: Command = {
  id: 'ui-designer-suggestion-2',
  title: 'Generate a residence address form',
  icon: CommandIcon.Action,
  action: () => {
    // TypeAreaEvents.promptSubmit(
    //   'Create address form, including the following fields: Country (dropdown), County (dropdown), City (dropdown), District (dropdown), Street, Street No, Building, Entrance, Floor, Apartment, Zip code; add a Continue button'
    // ),
  },
}

const uiDesignerSuggestion3Cmd: Command = {
  id: 'ui-designer-suggestion-3',
  title: 'Generate financial profile form',
  icon: CommandIcon.Action,
  action: () => {
    // TypeAreaEvents.promptSubmit(
    //   'Create form including: Education (dropdown), Occupation type (dropdown), Profession, Marital status (dropdown), Spouse SSN, No of dependents, Housing situation (dropdown), Monthly cost, Property type (dropdown), Seniority, Car owner switch, Political exposed person switch, Other spendings (textarea); add Continue button'
    // ),
  },
}

const uiDesignerSuggestion4Cmd: Command = {
  id: 'ui-designer-suggestion-4',
  title: 'Generate credit card application form',
  icon: CommandIcon.Action,
  action: () => {
    // TypeAreaEvents.promptSubmit(
    //   'Generate credit card application form with: type of credit card (radio button list with 3 options), Interest type (dropdown), Interest rate, Yearly admin fee, DAE, Credit card limit, Type of Card (dropdown), Card Color (dropdown), Validity period (dropdown)'
    // ),
  },
}

@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
  #store = inject(Store)
  #router = inject(Router)

  #applicationId = new BehaviorSubject<string>(null)
  #applicationVersionId = new BehaviorSubject<string>(null)

  // ANALYST AGENT

  #createNewProcessCmd: Command = {
    id: 'bpmn-analyst-create-new-process',
    title: 'Create New Business Process',
    icon: CommandIcon.Process,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateNewProcessCommand()),
  }

  #editProcessCmd: Command = {
    id: 'bpmn-analyst-edit-existing-process',
    title: 'Edit Current Business Process',
    icon: CommandIcon.Process,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateExistingProcessCommand()),
  }

  // AUDIT AGENT

  #checkProcessCmd: Command = {
    id: 'bpmn-analyst-check-existing-process',
    title: 'Audit this Business Process',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.checkExistingProcessCommand()),
  }

  #checkUICmd: Command = {
    id: 'ui-designer-check-existing-ui',
    title: 'Audit this User Interface',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.checkExistingUICommand(null)),
  }

  // INTEGRATOR AGENT

  #generateIntegrationSystemCmd: Command = {
    id: 'generate-integration-system',
    title: 'Generate Integration System',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateIntegrationSystemCommand()),
  }

  #generateIntegrationWorkflowCmd: Command = {
    id: 'generate-integration-workflow',
    title: 'Generate Integration Workflow',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateIntegrationWorkflowCommand()),
  }

  // ARCHITECT AGENT

  #generateDataModelCmd: Command = {
    id: 'generate-data-model',
    title: 'Generate Data Model',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateDataModelCommand()),
  }

  #generateImageCmd: Command = {
    id: 'generate-image',
    title: 'Generate Image',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateImageCommand()),
  }

  #generateNotificationCmd: Command = {
    id: 'generate-notification-template',
    title: 'Generate Notification Template',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateNotificationTemplateCommand()),
  }

  #generateDocumentCmd: Command = {
    id: 'generate-document-template',
    title: 'Generate Document Template',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateDocumentTemplateCommand()),
  }

  #generateEnumerationCmd: Command = {
    id: 'generate-enumeration',
    title: 'Generate Enumeration Items',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateEnumerationCommand()),
  }

  #translateSubstitutionTagsCmd: Command = {
    id: 'translate-substitution-tags',
    title: 'Translate Substitution Tags',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.translateSubstitutionTagCommand()),
  }

  // DESIGNER AGENT

  #generateUICmd: Command = {
    id: 'ui-designer-generate-ui',
    title: 'Generate User Interface',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => {
      const userTaskTemplateConfig = this.#store.selectSignal(selectedAiUserTaskTemplateConfig)()
      if (userTaskTemplateConfig) {
        this.#store.dispatch(
          CommandPaletteActions.generateNewUICommand({
            userTaskNavigationAreaId: userTaskTemplateConfig.navigationAreaId,
          })
        )
      }
    },
  }

  #createBusinessRule: Command = {
    id: 'br-developer-create-new-business-rule',
    title: 'Generate Business Rule',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateNewBusinessRuleCommand()),
  }

  #editBusinessRule: Command = {
    id: 'br-developer-edit-business-rule',
    title: 'Edit Business Rule',
    icon: CommandIcon.Action,
    shortcut: {
      icons: [
        { type: IconType.ICON, name: 'cmd-macos' },
        { type: IconType.KEY, name: AiKeyboardKeyCode.GENERATE },
      ],
    },
    action: () => this.#store.dispatch(CommandPaletteActions.generateNewBusinessRuleCommand()),
  }

  // ASSISTANT Suggestions

  #assistantSuggestion1: Command = {
    id: 'assistant-suggestion-1',
    title: 'How do I create my first FlowX process?',
    icon: CommandIcon.Info,
    action: () =>
      this.#store.dispatch(
        CommandPaletteActions.askAssistantCommand({
          question: 'How do I create my first FlowX process?',
          processDoc: false,
        })
      ),
  }

  #assistantSuggestion2: Command = {
    id: 'assistant-suggestion-2',
    title: 'What is the difference between an exclusive gateway and a parallel gateway?',
    icon: CommandIcon.Info,
    action: () =>
      this.#store.dispatch(
        CommandPaletteActions.askAssistantCommand({
          question: 'What is the difference between an exclusive gateway and a parallel gateway?',
          processDoc: false,
        })
      ),
  }

  #assistantSuggestion3: Command = {
    id: 'assistant-suggestion-3',
    title: 'How can I stop a token until a message is received?',
    icon: CommandIcon.Info,
    action: () =>
      this.#store.dispatch(
        CommandPaletteActions.askAssistantCommand({
          question: 'How can I stop a token until a message is received?',
          processDoc: false,
        })
      ),
  }

  #assistantSuggestion4: Command = {
    id: 'assistant-suggestion-4',
    title: 'How do I integrate a third party system?',
    icon: CommandIcon.Info,
    action: () =>
      this.#store.dispatch(
        CommandPaletteActions.askAssistantCommand({
          question: 'How do I integrate a third party system?',
          processDoc: false,
        })
      ),
  }

  #assistantSuggestion5: Command = {
    id: 'assistant-suggestion-5',
    title: 'What are the most common use cases for timer events?',
    icon: CommandIcon.Info,
    action: () =>
      this.#store.dispatch(
        CommandPaletteActions.askAssistantCommand({
          question: 'What are the most common use cases for timer events?',
          processDoc: false,
        })
      ),
  }

  #cmdBySelectedAgent = {
    [AiAgent.ASSISTANT]: () => [],
    [AiAgent.ANALYST]: () => [
      // bpmnAnalystSuggestion1Cmd,
      // bpmnAnalystSuggestion2Cmd,
      // bpmnAnalystSuggestion3Cmd,
      // bpmnAnalystSuggestion4Cmd,
    ],
    [AiAgent.DESIGNER]: () => [
      // uiDesignerSuggestion1Cmd,
      // uiDesignerSuggestion2Cmd,
      // uiDesignerSuggestion3Cmd,
      // uiDesignerSuggestion4Cmd,
    ],
    [AiAgent.DEVELOPER]: () => [],
  }

  getActionsByState(
    selectedAgent: AiAgent,
    data?: {
      contextAgent: AiAgent
      pageAgentAction: string
      aiPermissions: AiPermissions
      bpmnHasSwimlanesOrNodes: boolean
      bpmnReadonly: boolean
      businessRuleLanguageAiOptions: { disabled: boolean; tooltip: string }
      hasDesignerAiUserTaskTemplateConfig: boolean
    }
  ): Command[] {
    const {
      contextAgent,
      pageAgentAction,
      aiPermissions,
      bpmnReadonly,
      bpmnHasSwimlanesOrNodes,
      businessRuleLanguageAiOptions,
      hasDesignerAiUserTaskTemplateConfig,
    } = data

    const denyAnalystAccess = selectedAgent === AiAgent.ANALYST && !aiPermissions?.showAnalystAgent
    const denyDesignerAccess =
      selectedAgent === AiAgent.DESIGNER && !aiPermissions?.showDesignerAgent
    const denyDeveloperAccess =
      selectedAgent === AiAgent.DEVELOPER && !aiPermissions?.showDeveloperAgent

    if (denyAnalystAccess || denyDesignerAccess || denyDeveloperAccess) {
      return []
    } else {
      return (
        this.#defaultActions({
          contextAgent,
          pageAgentAction,
          aiPermissions,
          bpmnReadonly,
          bpmnHasSwimlanesOrNodes,
          businessRuleContext: null,
          businessRuleLanguageAiOptions,
          hasDesignerAiUserTaskTemplateConfig,
        }) ?? []
      )
    }
  }

  getCommandsByState(
    selectedAgent: AiAgent,
    data?: {
      contextAgent: AiAgent
      aiPermissions: AiPermissions
      bpmnHasSwimlanesOrNodes: boolean
      bpmnReadonly: boolean
      businessRuleContext: BusinessRuleContext | null
      businessRuleLanguageAiOptions: { disabled: boolean; tooltip: string }
      hasDesignerAiUserTaskTemplateConfig: boolean
    }
  ): Command[] {
    const {
      contextAgent,
      aiPermissions,
      bpmnReadonly,
      bpmnHasSwimlanesOrNodes,
      businessRuleContext,
      businessRuleLanguageAiOptions,
      hasDesignerAiUserTaskTemplateConfig,
    } = data

    const denyAnalystAccess = selectedAgent === AiAgent.ANALYST && !aiPermissions?.showAnalystAgent
    const denyDesignerAccess =
      selectedAgent === AiAgent.DESIGNER && !aiPermissions?.showDesignerAgent
    const denyDeveloperAccess =
      selectedAgent === AiAgent.DEVELOPER && !aiPermissions?.showDeveloperAgent

    if (denyAnalystAccess || denyDesignerAccess || denyDeveloperAccess) {
      return []
    } else {
      return (
        this.#defaultCommands({
          contextAgent,
          aiPermissions,
          bpmnReadonly,
          bpmnHasSwimlanesOrNodes,
          businessRuleContext,
          businessRuleLanguageAiOptions,
          hasDesignerAiUserTaskTemplateConfig,
        }) ?? []
      )
    }
  }

  setApplicationId(applicationId: string): void {
    this.#applicationId.next(applicationId)
  }

  getApplicationId(): string {
    return this.#applicationId.getValue()
  }

  setApplicationVersionId(applicationVersionId: string): void {
    this.#applicationVersionId.next(applicationVersionId)
  }

  getApplicationVersionId(): string {
    return this.#applicationVersionId.getValue()
  }

  navigateToObject(object): void {
    if (object.object_type === 'process') {
      this.goToProcess(
        this.#applicationId.getValue(),
        this.#applicationVersionId.getValue(),
        object
      )
    } else if (object.object_type === 'node') {
      this.goToNode(this.#applicationId.getValue(), this.#applicationVersionId.getValue(), object)
    } else if (object.object_type === 'ui') {
      this.goToUI(this.#applicationId.getValue(), this.#applicationVersionId.getValue(), object)
    } else if (object.object_type === 'action') {
      this.goToAction(this.#applicationId.getValue(), this.#applicationVersionId.getValue(), object)
    } else {
      this.goToProcess(
        this.#applicationId.getValue(),
        this.#applicationVersionId.getValue(),
        object
      )
    }
  }

  public async goToProcess(appId: string, appVersion: string, object: any): Promise<void> {
    const processLink = `/projects/${appId}/config/${appVersion}/processes/${object.process_id}/${object.process_id_int}/diagram`
    await this.navigate(processLink)
  }

  public async goToNode(appId: string, appVersion: string, object: any): Promise<void> {
    const nodeLink = `/projects/${appId}/config/${appVersion}/processes/${object.process_id}/${object.process_id_int}/diagram?nodePanelOpened=true&nodeId=${object.object_id}`
    await this.navigate(nodeLink)
  }
  public async goToUI(appId: string, appVersion: string, object: any): Promise<void> {
    const nodeLink = `/projects/${appId}/config/${appVersion}/processes/${object.process_id}/${object.process_id_int}/${object.object_id}/ui`
    await this.navigate(nodeLink)
  }

  public async goToAction(appId: string, appVersion: string, object: any): Promise<void> {
    const nodeLink = `/projects/${appId}/config/${appVersion}/processes/${object.process_id}/${object.process_id_int}/diagram?nodePanelOpened=true&nodeId=${object.parent_id}&businessRuleId=${object.object_id}`
    await this.navigate(nodeLink)
  }

  navigate(path: string): Promise<boolean> {
    return this.#router.navigateByUrl(path, { skipLocationChange: true })
  }

  #defaultCommands = (payload: {
    contextAgent: AiAgent
    aiPermissions: AiPermissions
    bpmnReadonly: boolean
    bpmnHasSwimlanesOrNodes: boolean
    businessRuleContext?: BusinessRuleContext | null
    businessRuleLanguageAiOptions: { disabled: boolean; tooltip: string }
    hasDesignerAiUserTaskTemplateConfig: boolean
  }): Command[] => {
    const {
      contextAgent,
      aiPermissions,
      bpmnReadonly,
      bpmnHasSwimlanesOrNodes,
      businessRuleContext,
      businessRuleLanguageAiOptions,
      hasDesignerAiUserTaskTemplateConfig,
    } = payload
    const hasExpression = !!businessRuleContext?.expression
    const defaultCmds = aiPermissions?.showAssistantAgent
      ? [
          this.#assistantSuggestion1,
          this.#assistantSuggestion2,
          this.#assistantSuggestion3,
          this.#assistantSuggestion4,
          this.#assistantSuggestion5,
        ]
      : []

    return [...defaultCmds]
  }

  #defaultActions = (payload: {
    contextAgent: AiAgent
    pageAgentAction: string
    aiPermissions: AiPermissions
    bpmnReadonly: boolean
    bpmnHasSwimlanesOrNodes: boolean
    businessRuleContext: BusinessRuleContext | null
    businessRuleLanguageAiOptions: { disabled: boolean; tooltip: string }
    hasDesignerAiUserTaskTemplateConfig: boolean
  }): Command[] => {
    const {
      contextAgent,
      pageAgentAction,
      aiPermissions,
      bpmnReadonly,
      businessRuleContext,
      bpmnHasSwimlanesOrNodes,
      businessRuleLanguageAiOptions,
      hasDesignerAiUserTaskTemplateConfig,
    } = payload
    const hasExpression = !!businessRuleContext?.expression
    const bpmnGenerateCmd = bpmnHasSwimlanesOrNodes
      ? [this.#editProcessCmd, this.#checkProcessCmd]
      : [this.#createNewProcessCmd]

    let architectCmd = []
    let integratorCmd = []
    switch (pageAgentAction) {
      case 'document-templates':
        architectCmd = [this.#generateDocumentCmd]
        break
      case 'notification-templates':
        architectCmd = [this.#generateNotificationCmd]
        break
      case 'enumerations':
        architectCmd = [this.#generateEnumerationCmd]
        break
      case 'substitution-tags':
        architectCmd = [this.#translateSubstitutionTagsCmd]
        break
      case 'data-model':
        architectCmd = [this.#generateDataModelCmd]
        break
      case 'image':
        architectCmd = [this.#generateImageCmd]
        break
      case 'integration-system':
        integratorCmd = [this.#generateIntegrationSystemCmd]
        break
      case 'integration-workflow':
        integratorCmd = [this.#generateIntegrationWorkflowCmd]
        break
      default:
        architectCmd = []
        break
    }

    const cmdByLocation = {
      [AiAgent.DESIGNER]:
        aiPermissions?.showDesignerAgent && !bpmnReadonly
          ? [
              {
                ...this.#generateUICmd,
                disabled: !hasDesignerAiUserTaskTemplateConfig,
              },
            ]
          : [],
      [AiAgent.ANALYST]:
        aiPermissions?.showAnalystAgent && !bpmnReadonly ? [...bpmnGenerateCmd] : [],
      [AiAgent.DEVELOPER]:
        aiPermissions?.showDeveloperAgent && !bpmnReadonly
          ? [
              {
                ...(hasExpression ? this.#editBusinessRule : this.#createBusinessRule),
                disabled: businessRuleLanguageAiOptions.disabled,
                tooltip: businessRuleLanguageAiOptions.tooltip || '',
              },
            ]
          : [],
      [AiAgent.ARCHITECT]: [...architectCmd],
      [AiAgent.INTEGRATOR]: [...integratorCmd],
    }
    const locationCommands = contextAgent ? cmdByLocation[contextAgent] : []

    return locationCommands
  }
}
